<template>
  <div>
    <Head title="Purchase Orders" />
    <h1 class="mb-8 text-3xl font-bold">
      Transaksi
      <span class="font-medium text-indigo-400"> / </span>
      Purchase Orders
    </h1>
    <div class="flex justify-between items-center mb-6">
      <search-filter
        v-model="form.search"
        class="mr-4 w-full max-w-md"
        @reset="reset">
        <label class="block text-gray-700">Project:</label>
        <select v-model="form.project" class="mt-1 w-full form-select">
          <option :value="null">
            [ALL]
          </option>
          <option
            v-for="project in projects"
            :key="project.id"
            :value="project.id">
            {{ project.title }}
          </option>
        </select>
      </search-filter>
      <div class="flex mx-6 w-full bg-white rounded shadow">
        <vSelect
          v-model="form.item"
          :reduce="(item) => item.id"
          class="min-w-full focus:text-indigo-500"
          placeholder="[Filter by barang detail...]"
          label="name"
          :filterable="false"
          :options="options"
          :append-to-body="true"
          @search="onSearchItem" />
      </div>
      <Link class="btn-indigo" href="/purchaseorders/create">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Purchase Order</span>
      </Link>
    </div>
    <div class="overflow-x-auto bg-white rounded-md shadow">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left">
            <th class="px-6 pt-6 pb-4">
              Nomor
            </th>
            <th class="px-6 pt-6 pb-4">
              Lampiran
            </th>
            <th class="px-6 pt-6 pb-4">
              Project
            </th>
            <th class="px-6 pt-6 pb-4">
              Activity
            </th>
            <th class="px-6 pt-6 pb-4">
              Vendor
            </th>
            <th class="px-6 pt-6 pb-4">
              Operator
            </th>
            <th class="px-6 pt-6 pb-4">
              Total
            </th>
            <th class="px-6 pt-6 pb-4">
              Status
            </th>
            <th class="px-6 pt-6 pb-4" colspan="2">
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="purchaseorder in purchaseorders.data"
            :key="purchaseorder.id"
            class="focus-within:bg-gray-100 hover:bg-gray-100">
            <td class="border-t">
              <Link class="flex items-center py-4 px-6 focus:text-indigo-500" :href="`/purchaseorders/${purchaseorder.id}/edit`">
                {{ purchaseorder.code }}
                <icon
                  v-if="purchaseorder.deleted_at"
                  name="trash"
                  class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex py-4 px-10"
                :href="`/purchaseorders/${purchaseorder.id}/edit#attachments`"
                tabindex="-1">
                <icon name="attachment" v-if="purchaseorder.has_attachment" />
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link
                class="flex items-center py-4 px-6"
                :href="`/purchaseorders/${purchaseorder.id}/edit`"
                tabindex="-1">
                {{ purchaseorder.project }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link
                class="flex items-center py-4 px-6"
                :href="`/purchaseorders/${purchaseorder.id}/edit`"
                tabindex="-1">
                {{ purchaseorder.pr_activity }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link
                class="flex items-center py-4 px-6"
                :href="`/purchaseorders/${purchaseorder.id}/edit`"
                tabindex="-1">
                {{ purchaseorder.supplier }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link
                class="flex items-center py-4 px-6"
                :href="`/purchaseorders/${purchaseorder.id}/edit`"
                tabindex="-1">
                {{ purchaseorder.operator }}
              </Link>
            </td>
            <td class="text-right border-t">
              {{ formatNumber(purchaseorder.total) }}
            </td>
            <td class="border-t">
              <Link
                class="flex items-center py-4 px-6"
                :href="`/purchaseorders/${purchaseorder.id}/edit`"
                tabindex="-1">
                {{ purchaseorder.status }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link class="flex items-center py-4 px-6 focus:text-indigo-500" :href="`/purchaseorders/${purchaseorder.id}/edit`">
                {{ purchaseorder.description }}
                <icon
                  v-if="purchaseorder.deleted_at"
                  name="trash"
                  class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="w-px border-t">
              <Link
                class="flex items-center px-4"
                :href="`/purchaseorders/${purchaseorder.id}/edit`"
                tabindex="-1">
                <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="purchaseorders.data.length === 0">
            <td class="py-4 px-6 border-t" colspan="5">
              No purchase request found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="purchaseorders.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import { ref } from 'vue'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
    components: {
        Head,
        Icon,
        Link,
        Pagination,
        SearchFilter,
        vSelect,
    },
    layout: Layout,
    props: {
        projects: Object,
        filters: Object,
        purchaseorders: Object,
        defaultOptions: Object,
    },
    setup (props) {
        const options = ref(props.defaultOptions)
        const searchAutoComplete = throttle((loading, search) => {
            fetch(`/items?options=1&search=${encodeURI(search)}`).then((res) => {
                res.json().then((json) => (options.value = json.items))
                loading(false)
            })
        }, 500)
        const onSearchItem = (search, loading) => {
            if (search.length) {
                loading(true)
                searchAutoComplete(loading, search)
            }
        }
        return {
            options,
            onSearchItem,
        }
    },
    data () {
        return {
            form: {
                search: this.filters.search,
                project: this.filters.project,
                item: this.filters.item ? parseInt(this.filters.item) : null,
            },
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get('/purchaseorders', pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        formatNumber (num) {
            return num ? num.toLocaleString('id-ID') : '0'
        },
        reset () {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
